import React from 'react';
import {
    PriceTicker,
    AlertFeed,
    PerformanceStats,
    AboutFounder
} from '../components/trader';
import '../themes/trader.css';

// ============================================
// Type Definitions
// ============================================
interface TickerItem {
    symbol: string;
    name: string;
    price: number;
    change24h: number;
    icon?: string;
}

interface Alert {
    id: string;
    type: 'alert' | 'news';
    content: string;
    timestamp: Date;
}

interface Stat {
    value: string;
    label: string;
}

export interface TraderTemplateData {
    identity: {
        name: string;
        handle: string;
        pfp: string;
    };
    hero: {
        prelabel: string;
        headline: string;
        highlightWord?: string;
        subline: string;
        ctaText: string;
        ctaLink: string;
    };
    ticker: TickerItem[];
    stats: Stat[];
    tradingViewSymbol?: string;
    alerts: Alert[];
    newsletter?: {
        title: string;
        description: string;
        placeholder?: string;
        buttonText?: string;
    };
    founder: {
        name: string;
        handle: string;
        avatar: string;
        bio: string;
        achievements: { value: string; label: string }[];
    };
    social: {
        twitter?: string;
        discord?: string;
        telegram?: string;
        youtube?: string;
    };
}

// ============================================
// Social Icons Component
// ============================================
const SocialIcon: React.FC<{ platform: string; href: string }> = ({ platform, href }) => {
    const icons: Record<string, string> = {
        twitter: '𝕏',
        discord: '💬',
        telegram: '📬',
        youtube: '▶️',
    };

    return (
        <a href={href} target="_blank" rel="noopener noreferrer" title={platform}>
            {icons[platform] || '🔗'}
        </a>
    );
};

// ============================================
// Main Template Component
// ============================================
export const TraderTemplate: React.FC<{ data: TraderTemplateData }> = ({ data }) => {
    const { identity, hero, ticker, stats, tradingViewSymbol, alerts, newsletter, founder, social } = data;

    // Parse headline with highlight word
    const renderHeadline = () => {
        if (!hero.highlightWord) {
            return hero.headline;
        }
        const parts = hero.headline.split(hero.highlightWord);
        return (
            <>
                {parts[0]}
                <span className="highlight">{hero.highlightWord}</span>
                {parts.slice(1).join(hero.highlightWord)}
            </>
        );
    };

    return (
        <div className="trader-layout" data-theme="trader">
            {/* Price Ticker */}
            <PriceTicker items={ticker} />

            {/* Header */}
            <header className="trader-header">
                <div className="trader-header-logo">
                    <span className="trader-header-logo-icon">📈</span>
                    <span>{identity.handle}</span>
                </div>

                <nav className="trader-header-nav">
                    <a href="#chart">Chart</a>
                    <a href="#feed">Alpha</a>
                    <a href="#about">About</a>
                </nav>

                <a
                    href={social.twitter || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="trader-header-cta"
                >
                    Follow
                </a>
            </header>

            {/* Hero Section */}
            <section className="trader-hero">
                <div className="trader-hero-prelabel">{hero.prelabel}</div>
                <h1 className="trader-hero-headline">{renderHeadline()}</h1>
                <p className="trader-hero-subline">{hero.subline}</p>
                <a href={hero.ctaLink} className="trader-hero-cta">
                    {hero.ctaText}
                </a>
            </section>

            {/* Performance Stats */}
            {stats.length > 0 && <PerformanceStats stats={stats} />}

            {/* Main Content: Chart + Feed */}
            <div className="trader-content">
                {/* TradingView Chart */}
                <div id="chart" className="trader-chart-section">
                    <div className="trader-chart-header">
                        <span className="trader-chart-title">Live Chart</span>
                        <span className="trader-chart-pair">{tradingViewSymbol || 'BTCUSDT'}</span>
                    </div>
                    <div className="trader-chart-embed">
                        {/* TradingView Widget Placeholder */}
                        <iframe
                            src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=${tradingViewSymbol || 'BINANCE:BTCUSDT'}&interval=D&hidesidetoolbar=1&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=[]&theme=dark&style=1&timezone=Etc%2FUTC`}
                            title="TradingView Chart"
                            allowFullScreen
                        />
                    </div>
                </div>

                {/* Alpha Feed + Newsletter */}
                <div id="feed">
                    <AlertFeed alerts={alerts} />

                    {newsletter && (
                        <div className="trader-newsletter">
                            <div className="trader-newsletter-title">{newsletter.title}</div>
                            <div className="trader-newsletter-desc">{newsletter.description}</div>
                            <form className="trader-newsletter-form" onSubmit={(e) => e.preventDefault()}>
                                <input
                                    type="email"
                                    className="trader-newsletter-input"
                                    placeholder={newsletter.placeholder || 'Enter your email'}
                                />
                                <button type="submit" className="trader-newsletter-btn">
                                    {newsletter.buttonText || 'Subscribe'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Social Links */}
                    <div className="trader-social">
                        {social.twitter && <SocialIcon platform="twitter" href={social.twitter} />}
                        {social.discord && <SocialIcon platform="discord" href={social.discord} />}
                        {social.telegram && <SocialIcon platform="telegram" href={social.telegram} />}
                        {social.youtube && <SocialIcon platform="youtube" href={social.youtube} />}
                    </div>
                </div>
            </div>

            {/* About Founder */}
            <section id="about">
                <AboutFounder
                    name={founder.name}
                    handle={founder.handle}
                    avatar={founder.avatar}
                    bio={founder.bio}
                    achievements={founder.achievements}
                />
            </section>

            {/* Footer */}
            <footer className="trader-footer">
                <p className="trader-footer-text">
                    © {new Date().getFullYear()} {identity.name} • Not financial advice • Built with Crypto Factory
                </p>
            </footer>
        </div>
    );
};

export default TraderTemplate;
